import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

/**
 * AuthContext — Source de vérité auth côté frontend (Sprint Frontend B3 → B5
 * + Phase 6 / G2 cutover endpoints TS).
 *
 * Backend (B1+B2) émet des cookies httpOnly `admin-token` et `agent-token`
 * sur login, et les middlewares les lisent en priorité. Le frontend ne
 * stocke plus AUCUN token : on s'appuie sur le cookie httpOnly (envoyé
 * automatiquement par le navigateur via `credentials:'include'`).
 *
 * Phase 6 / G2 — migration vers les endpoints TS (`/api/auth/*`) :
 *   - login()       → POST /api/auth/login         (endpoint unifié email+password,
 *                                                   le backend détermine le role
 *                                                   depuis le User en BDD)
 *   - logout()      → POST /api/auth/logout        (route unique — le backend
 *                                                   clear les 2 cookies selon
 *                                                   ce qui est présent)
 *   - checkAuth()   → GET  /api/auth/me            (inchangé)
 *
 * Auth refacto étape 5 — login signature `login(email, password, expectedRole?)` :
 *   - `expectedRole` est optionnel : si fourni (ex : page AdminLogin), on vérifie
 *     que le role retourné par le backend matche, sinon on throw (un agent qui
 *     tente de se logger sur /admin doit être rejeté).
 *
 * État :
 *   - status : 'loading' | 'authenticated' | 'unauthenticated'
 *   - role   : 'admin' | 'agent' | null
 *
 * B5 — Auto-logout après 1h d'inactivité :
 *   - tracker last activity via mousedown/keydown/touchstart/scroll
 *   - check toutes les 60s si Date.now() - lastActivity > 1h → logout()
 */

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const CHECK_INTERVAL_MS = 60 * 1000;          // 1 minute

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({ status: 'loading', role: null });

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAuth({ status: 'authenticated', role: data.role || null });
      } else {
        setAuth({ status: 'unauthenticated', role: null });
      }
    } catch {
      // Network error ou endpoint pas encore créé (B4) → unauth
      setAuth({ status: 'unauthenticated', role: null });
    }
  }, []);

  const login = useCallback(async (email, password, expectedRole) => {
    // Auth refacto étape 5 — endpoint unifié. Le backend détermine le role
    // (admin/agent) à partir du User en BDD et pose le bon cookie.
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Identifiants invalides');
    }
    const data = await res.json().catch(() => ({}));
    // expectedRole optionnel : si l'UI demande un role spécifique (page
    // AdminLogin ou AgentLogin), on rejette si le user n'a pas le bon role.
    if (expectedRole && data.role !== expectedRole) {
      throw new Error('Rôle incorrect pour cette interface');
    }
    setAuth({ status: 'authenticated', role: data.role });
  }, []);

  const logout = useCallback(async () => {
    // Phase 6 / G2 — route unique côté TS : le backend clear les 2 cookies
    // selon ce qui est présent (cf. AuthController.logout).
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
    setAuth({ status: 'unauthenticated', role: null });
  }, []);

  // Auto-check au mount
  useEffect(() => { checkAuth(); }, [checkAuth]);

  // B5 — Track inactivité via ref (pas de re-render à chaque mouvement souris)
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const updateActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, updateActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, updateActivity));
  }, []);

  // B5 — Auto-logout après 1h d'inactivité (check toutes les 60s)
  useEffect(() => {
    if (auth.status !== 'authenticated') return undefined;
    const id = setInterval(() => {
      if (Date.now() - lastActivityRef.current > INACTIVITY_TIMEOUT_MS) {
        // eslint-disable-next-line no-console
        console.warn('[auth] auto-logout: 1h d\'inactivité dépassée');
        logout();
      }
    }, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [auth.status, logout]);

  return (
    <AuthContext.Provider value={{ ...auth, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
